import { addWeeks, isPast } from "date-fns";
import { whiteListedSchools } from "@/lib/util";
import Link from "next/link";
import { StripeCustomerData } from "@/pages/api/stripe-customer";
import { useEffect, useState } from "react";
import styled from "styled-components";
import useSWR, { mutate } from "swr";
import { CurrentUser, UserContext } from "./Page";
import UndrawTimeManagement from "./svg/UndrawTimeManagement";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 500px;
  margin: auto;
  text-align: center;

  & > * {
    margin: 10px;
  }
`;

interface CccProps {
  user: CurrentUser;
  children: React.ReactNode;
}

const Ccc: React.FC<CccProps> = ({ user: { roles, school }, children }) => {
  const [hasResult, setHasResult] = useState(false);
  useEffect(() => {
    const id = school && school.customerID;

    mutate("/api/stripe-customer?id=" + id);
  }, []);

  const isTeacher = roles.some((r) => r.name == "teacher");
  const isAdmin = roles.some((r) => r.name == "admin");

  const expiryDate = school
    ? school.willExpire
      ? new Date(school.willExpire)
      : addWeeks(new Date(school.createdAt), 10)
    : new Date();
  const hasExpired = isPast(expiryDate);

  const id = school && school.customerID;

  const { data } = useSWR<StripeCustomerData>("/api/stripe-customer?id=" + id, {
    fallbackData: {},
    onSuccess: () => {
      setHasResult(true);
    },
  });

  return hasResult &&
    school &&
    !whiteListedSchools.includes(school.name) &&
    !data?.subscription &&
    hasExpired ? (
    <Container>
      <UndrawTimeManagement height="200px" />
      <h1>{school.name}&#39;s trial period has expired</h1>
      {isAdmin && (
        <p>
          To continue using ClassCart, please add a payment method or request an
          invoice from the{" "}
          <Link prefetch={false} href="/settings">
            settings page
          </Link>
          .
        </p>
      )}
      {isTeacher && (
        <p>
          If you need more time to trial the program let us know at{" "}
          <a
            href="mailto:support@classcart.com"
            className="text-blue-500 hover:underline"
          >
            support@classcart.com
          </a>
        </p>
      )}
    </Container>
  ) : (
    <>{children}</>
  );
};

interface Props {
  children: React.ReactNode;
}

const CheckIfTrial: React.FC<Props> = ({ children }) => {
  return (
    <UserContext.Consumer>
      {(user) => <Ccc user={user}>{children}</Ccc>}
    </UserContext.Consumer>
  );
};

export default CheckIfTrial;
